import type { CSSProperties, FC, ReactNode } from 'react';
import { Badge } from 'antd';
import type { BadgeProps } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import type { PresenceStatus } from '@/types/realtime';

type ActivityStatus = 'resource:view' | 'resource:edit' | 'idle';
type StatusBadgeKind = 'activity' | 'presence';
type StatusBadgeStatus = ActivityStatus | PresenceStatus | undefined;

interface StatusBadgeProps {
    borderColor?: string;
    borderWidth?: number;
    children?: ReactNode;
    dot?: boolean;
    dotSize?: CSSProperties['width'];
    kind?: StatusBadgeKind;
    offset?: [number, number];
    size?: BadgeProps['size'];
    status?: StatusBadgeStatus;
    style?: CSSProperties;
    text?: ReactNode;
}

const mapPresenceStatusToAntBadgeStatus = {
    online: 'success',
    away: 'warning',
    offline: 'default',
} as Record<PresenceStatus, BadgeProps['status']>;

const mapActivityToAntBadgeStatus = {
    'resource:view': 'success',
    'resource:edit': 'success',
    idle: 'warning',
} as Record<ActivityStatus, BadgeProps['status']>;

const isPresenceStatus = (status: StatusBadgeStatus): status is PresenceStatus =>
    status === 'online' || status === 'away' || status === 'offline';

const isActivityStatus = (status: StatusBadgeStatus): status is ActivityStatus =>
    status === 'resource:view' || status === 'resource:edit' || status === 'idle';

const StatusBadge: FC<StatusBadgeProps> = ({
    borderColor,
    borderWidth = 1,
    children,
    dot = true,
    dotSize,
    kind = 'presence',
    offset,
    size = 'small',
    status,
    style,
    text,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const resolvedStatus = status ?? (kind === 'presence' ? 'offline' : undefined);
    const antStatus =
        kind === 'activity' && isActivityStatus(resolvedStatus)
            ? mapActivityToAntBadgeStatus[resolvedStatus]
            : isPresenceStatus(resolvedStatus)
              ? mapPresenceStatusToAntBadgeStatus[resolvedStatus]
              : 'default';
    const color =
        kind === 'activity'
            ? resolvedStatus === undefined
                ? token.colorBgLayout
                : resolvedStatus === 'idle'
                  ? token.yellow6
                  : token.green6
            : resolvedStatus === 'online'
              ? token.green6
              : resolvedStatus === 'away'
                ? token.yellow6
                : token.colorBgLayout;
    const resolvedDotSize = typeof dotSize === 'number' ? `${dotSize}px` : dotSize;
    const isMetadataBadge = Boolean(text) && !children;
    const dotStyle = isMetadataBadge
        ? style
        : {
              boxShadow: 'none',
              zIndex: 1,
              border: borderColor ? `${borderWidth}px solid ${borderColor}` : undefined,
              backgroundColor: color,
              ...(resolvedDotSize
                  ? {
                        width: resolvedDotSize,
                        height: resolvedDotSize,
                        minWidth: resolvedDotSize,
                    }
                  : undefined),
              ...style,
          };

    return (
        <Badge
            color={isMetadataBadge ? color : undefined}
            dot={dot}
            offset={offset}
            size={size}
            status={antStatus}
            text={text}
            style={dotStyle}
        >
            {children}
        </Badge>
    );
};

export default StatusBadge;
